import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, Loader2, Upload } from "lucide-react";

const FIELDS = [["phone", "Phone number"], ["license_number", "Florida real estate license #"], ["license_expiration", "License expiration date"],
  ["brokerage", "Brokerage"], ["broker_name", "Broker of record"], ["office_address", "Office address"], ["years_experience", "Years licensed"],
  ["service_areas", "Counties / cities you serve"], ["eo_carrier", "E&O insurance carrier"], ["eo_policy_number", "E&O policy #"], ["eo_expiration", "E&O expiration date"]];
const KEYS = [...FIELDS.map(([k]) => k), "extra"];
const DOCS = [["license", "Real estate license"], ["eo", "E&O insurance certificate"]];

const Doc = ({ k, label, doc, act }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    await act(() => api.post(`/rt/reo/docs/${k}`, fd), `${label} uploaded`);
    setBusy(false);
  };
  const view = async () => {
    try { const r = await api.get(`/rt/reo/docs/${k}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3" data-testid={`reo-doc-${k}`}>
      <div className="min-w-0 text-sm"><div className="font-semibold text-slate-900">{label}</div>
        {doc ? <button type="button" onClick={view} className="flex items-center gap-1 break-all text-xs text-blue-700 underline" data-testid={`reo-doc-view-${k}`}><FileText className="h-3 w-3 flex-none" />{doc.filename}</button>
          : <div className="text-xs text-slate-500">Not uploaded</div>}</div>
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid={`reo-doc-upload-${k}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1 h-4 w-4" />{doc ? "Replace" : "Upload"}</>}</Button>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} data-testid={`reo-doc-file-${k}`} />
    </div>
  );
};

export const ReoProfile = ({ d, act }) => {
  const [f, setF] = useState(d.profile);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty) setF(d.profile); }, [d.profile, dirty]);
  const edit = (k, v) => { setF((x) => ({ ...x, [k]: v })); setDirty(true); };
  const save = async () => { if (await act(() => api.put("/rt/reo/profile", Object.fromEntries(KEYS.map((k) => [k, f[k] || ""]))), "Profile saved")) setDirty(false); };
  return (
    <Card className="mb-5 p-6" data-testid="reo-profile-card">
      <h3 className="font-display text-lg font-bold text-slate-900">Your REO application details</h3>
      <p className="mb-4 text-xs text-slate-500">Ava uses these on every application. She signs up with {d.account_email}, {d.phone || "your profile phone"} and this password: <b data-testid="reo-portal-password">{d.profile.portal_password}</b></p>
      <div className="grid gap-3 sm:grid-cols-2">
        {FIELDS.map(([k, l]) => <div key={k}><Label>{l}</Label><Input className="mt-1" value={f[k] || ""} onChange={(e) => edit(k, e.target.value)} data-testid={`reo-profile-${k}`} /></div>)}
        <div className="sm:col-span-2"><Label>Anything else applications may ask (designations, E&amp;O policy details, REO experience)</Label>
          <Textarea className="mt-1" rows={3} value={f.extra || ""} onChange={(e) => edit("extra", e.target.value)} data-testid="reo-profile-extra" /></div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} data-testid="reo-profile-save">Save details</Button>
        {dirty && <span className="text-xs text-amber-700" data-testid="reo-profile-unsaved">Unsaved changes</span>}
      </div>
      <h4 className="mb-2 mt-6 text-sm font-bold text-slate-900">Documents</h4>
      <p className="mb-3 text-xs text-slate-500">Ava uploads these when a website asks for them. PDF, JPG or PNG, up to 10 MB.</p>
      <div className="grid gap-3 sm:grid-cols-2">{DOCS.map(([k, l]) => <Doc key={k} k={k} label={l} doc={d.docs?.[k]} act={act} />)}</div>
    </Card>
  );
};
